import { ALERT_LEVEL, getAlertLevelIndex } from "@const/alerting.constants";
import { AlertHistory, AlertHistoryDTO, AlertLevel, AlertOverlimitItemsDTO } from "@dto/alerting.dto";
import { OverlimitItem } from "@dto/architecture.dto";
import { Metric } from "@dto/pageResponse.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { UnitUtil } from "@dto/util/UnitUtil";
import { uniqueArrayFilterFce } from "@dto/util/util";
import { CustomTagProps } from '@rc-component/select/es/BaseSelect';
import { Input, List, Typography } from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import { ColumnFilterItem } from "antd/es/table/interface";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import XmRangeDatePicker, { RANGE_DATE_PICKER_DEFAULT_PRESETS } from "src/component/dataTimePicker/XmRangeDatePicker";
import { GridScope } from "src/component/grid/Grid";
import { ColProps } from "src/component/grid/gridTypes";
import { XmModal } from "src/component/modal/XmModal";
import { XmSelect } from "src/component/select/XmSelect";
import { makeItemLabelRoot } from "src/component/selectItems/children/Items";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import { API_URL } from "src/data/Api";
import { Log } from "src/service/log.service";
import { TextUtil } from "src/util/TextUtil";
import { UnitHelper } from "src/util/UnitHelper";
import { getApi } from "src/util/apiCalls";
import { nameof } from "ts-simple-nameof";

const getHistoryItemLabel = (item: OverlimitItem, metric: Metric, stringOnly: boolean = false) => {
  const isANumber = typeof item?.overlimit === 'number' || typeof item?.overlimit === 'string' && !isNaN(Number(item.overlimit));
  const overlimitPart = isANumber && item?.overlimit ? ` [${metric.unit !== "count" ? UnitUtil.formatSI(Number(item.overlimit), 2, false, metric) : item.overlimit}]` : item?.overlimit ? ` [${item.overlimit}]` : '';
  return !stringOnly ? <span>{makeItemLabelRoot(item, item.url ? true : false)}{overlimitPart}</span> : item.label + (item.root_parent ? ` (${item.root_parent.label})` : '') + overlimitPart;
};

const AlertingHistoryModal: FC<{ labels: OverlimitItem[], isModalOpen: boolean, setIsModalOpen: Dispatch<SetStateAction<boolean>>, metric: Metric, count: number; }> = ({ labels, isModalOpen, setIsModalOpen, metric, count }) => {

  const [searchTerm, setSearchTerm] = useState("");


  // Filtered list based on search term
  const filteredData = labels.filter(label =>
    (getHistoryItemLabel(label, metric, true) as string).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setSearchTerm("");
  };

  return <XmModal
    title={`Alert History Items (${labels.length})${count && count - labels.length > 0 ? ` (${count - labels.length} deleted)` : ''}`}
    open={isModalOpen}
    onCancel={closeModal}
    onOk={closeModal}
    cancelButtonProps={{ style: { display: 'none' } }}
  >
    <Input.Search
      placeholder="Search..."
      allowClear
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
      style={{ marginBottom: 16 }}
    />

    <List<OverlimitItem>
      bordered
      dataSource={filteredData}
      style={{
        maxHeight: "60vh",   // limit the scrollable height
        overflowY: "auto",   // make only the body scrollable
      }}
      renderItem={i => <List.Item>{getHistoryItemLabel(i, metric)}</List.Item>}
    />
  </XmModal>;
};

const AlertingHistoryItems: FC<{ alerting_history_id: string, metric: Metric, count: number; }> = ({ alerting_history_id, metric, count }) => {
  const [items, setItems] = useState<OverlimitItem[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchItems = async () => {
    if (showLoader) return; // Prevent multiple fetches
    setShowLoader(true);
    try {
      if (!items?.length) {
        const resp = await axios.get<AlertOverlimitItemsDTO>(API_URL.ALERTING + '/history/items/' + alerting_history_id);
        setItems(resp.data.data);
      }
      setIsModalOpen(true);
    } catch (error) {
      Log.error('Failed to fetch alert history items', error);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      <AlertingHistoryModal
        labels={items}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        metric={metric}
        count={count}
      />
      <span className="xm-lazy-load-items" onClick={fetchItems}>{showLoader ? <XmSpinner size="small" style={{ zIndex: 99 }} /> : <Typography.Link>{`${count}...`}</Typography.Link>}</span>
    </>);
};

export const AlertingHistory: FC = props => {
  const [gs] = useState(() => new GridScope<AlertHistory>);
  const divRef = useRef<HTMLDivElement>();
  const [history, setHistory] = useState<AlertHistory[]>([]);
  const [alertGroupFilter, setAlertGroupFilter] = useState<ColumnFilterItem[]>([]);
  const [alertGroupTypeFilter, setAlertGroupTypeFilter] = useState<ColumnFilterItem[]>([]);
  const [metricFilter, setMetricFilter] = useState<ColumnFilterItem[]>([]);
  const [itemFilter, setItemFilter] = useState<ColumnFilterItem[]>([]);
  const [userGroupFilter, setUserGroupFilter] = useState<ColumnFilterItem[]>([]);
  const [targetFilter, setTargetFilter] = useState<ColumnFilterItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [state] = useState<{ start: Date, end: Date, aborter: AbortController; }>(() => {
    const start = new Date();
    start.setDate(start.getDate() - 1);
    const end = new Date();
    return { aborter: null, start, end };
  });
  const severityFilter = useMemo<ColumnFilterItem[]>(() => Object.entries(ALERT_LEVEL).map(value => ({ text: value[0], value: value[1] })), []);

  useEffect(() => {
    updateHistory();
    return () => {
      state.aborter.abort();
    };
  }, []);

  function updateHistory() {
    state.aborter?.abort();
    state.aborter = new AbortController();
    setLoading(true);
    getApi<AlertHistoryDTO>(API_URL.ALERTING + '/history', state.aborter.signal,
      { params: { from: state.start, to: state.end } }).then(resp => {
        const hist = resp.data.data;
        hist.sort(triggeredSorter);
        hist.reverse();
        hist.forEach(ah => {
          ah.targets.sort((a, b) => a.length - b.length);
          ah.items.sort((a, b) => a.label.length - b.label.length);
        });
        setHistory(hist);

        const agTypeFilter = hist.filter(uniqueArrayFilterFce((a, b) => a.alerting_group_alert_type === b.alerting_group_alert_type))
          .sort(alertGroupTypeSorter)
          .map(ah => ({ text: ah.alerting_group_alert_type, value: ah.alerting_group_alert_type }));
        setAlertGroupTypeFilter(agTypeFilter);

        const agFilter = hist.filter(uniqueArrayFilterFce((a, b) => a.alerting_group_id === b.alerting_group_id))
          .sort(alertGroupSorter)
          .map(ah => ({ text: ah.alerting_group_label, value: ah.alerting_group_label }));
        setAlertGroupFilter(agFilter);

        const mFilter = hist.filter(uniqueArrayFilterFce((a, b) => a.metric.label === b.metric.label))
          .sort(metricSorter)
          .map(ah => ({ text: ah.metric.label, value: ah.metric.label }));
        setMetricFilter(mFilter);

        const iFilter = hist.flatMap(ah => ah.items).filter(uniqueArrayFilterFce((a, b) => a.label === b.label))
          .sort((a, b) => a.label.localeCompare(b.label))
          .map(i => ({ text: i.label, value: i.label }));
        setItemFilter(iFilter);

        const ugFilter = hist.filter(uniqueArrayFilterFce((a, b) => a.group_label === b.group_label))
          .sort(userGroupSorter)
          .map(ah => ({ text: ah.group_label, value: ah.group_label }));
        setUserGroupFilter(ugFilter);

        const tFilter = hist.flatMap(ah => ah.targets).filter(uniqueArrayFilterFce((a, b) => a === b))
          .sort((a, b) => a.localeCompare(b))
          .map(t => ({ text: t, value: t }));
        setTargetFilter(tFilter);

      }, (reason: AxiosError) => Log.error('Failed to load alert history!', reason))
      .finally(() => setLoading(false));
  }

  const cols: ColProps<AlertHistory>[] = [
    {
      key: 'time',
      title: 'Time',
      minWidth: '8em',
      dataIndex: path(t => t.triggered),
      //defaultSortOrder: 'descend',
      sorter: triggeredSorter,
      render: (value, record) => DateUtil.getShortDateTime(value)
    },
    {
      key: 'alertType',
      title: 'Alert Type',
      minWidth: '12em',
      dataIndex: path(t => t.alerting_group_alert_type),
      sorter: alertGroupTypeSorter,
      filters: alertGroupTypeFilter,
      filterSearch: true,
      onFilter: (value: string, record) => record.alerting_group_alert_type === value,
      render: (value, record) => TextUtil.trimEnd(value, 64)
    },
    {
      key: 'alertGroup',
      title: 'Alerting Group',
      minWidth: '12em',
      dataIndex: path(t => t.alerting_group_label),
      sorter: alertGroupSorter,
      filters: alertGroupFilter,
      filterSearch: true,
      onFilter: (value: string, record) => record.alerting_group_label === value,
      render: (value, record) => TextUtil.trimEnd(value, 64)
    },
    {
      key: 'severity',
      title: 'Severity',
      minWidth: '7em',
      dataIndex: path(t => t.level),
      sorter: (a, b) => getAlertLevelIndex(a.level) - getAlertLevelIndex(b.level),
      filters: severityFilter,
      onFilter: (value: AlertLevel, record) => record.level === value,
    },
    {
      key: 'metric',
      title: 'Metric',
      minWidth: '10em',
      dataIndex: path(t => t.metric.label),
      sorter: metricSorter,
      filters: metricFilter,
      filterSearch: true,
      onFilter: (value, record) => record.metric.label === value,
      render: (value, record) => {
        const m = record.metric;
        const prefix = m.defaultPrefix || UnitUtil.sizeUnit(0, m.shortcut, m.unit).unitSizePrefix;
        const prefixShortcut = (prefix || '') + m.shortcut;
        return UnitHelper.getMetricOptionLabel(record.metric.label, prefixShortcut);
      }
    },
    {
      key: 'limit',
      title: 'Limit',
      dataIndex: path(t => t.limit),
      sorter: (a, b) => a.limit - b.limit,
      render: (value, record) => UnitUtil.roundValue(UnitUtil.convertSImetric(record.limit, record.metric), 1, record.metric.unit, record.metric)
    },
    {
      key: 'items',
      title: 'Items',
      minWidth: '12em',
      className: 'xm-col-no-pad',
      dataIndex: path(t => t.items.length),
      noTooltip: true,
      render: (value, record) => <AlertingHistoryItems alerting_history_id={record.alerting_history_id} metric={record.metric as Metric} count={record.count} />,
    },
    {
      key: 'groupShared',
      title: 'Group Shared',
      minWidth: '10em',
      dataIndex: path(t => t.group_label),
      sorter: userGroupSorter,
      filters: userGroupFilter,
      filterSearch: true,
      onFilter: (value, record) => record.group_label === value
    },
    {
      key: 'targets',
      width: '15em',
      // className: 'xm-col-no-pad',
      title: 'Targets',
      dataIndex: path(t => t.targets.length),
      filters: targetFilter,
      filterSearch: true,
      noTooltip: true,
      onFilter: (value, record) => record.targets.some(t => t === value),
      render: (value, record) => <XmSelect ignoreReadonly classNames={{ popup: { root: "xm-select-readonly" } }} mode="multiple" maxTagCount="responsive" options={record.targets.map(t => ({ value: t, label: t }))}
        style={{ width: '100%' }} value={record.targets}
        menuItemSelectedIcon={null} removeIcon={''}
      />
    }
  ];

  const handleRangeChange: RangePickerProps['onChange'] = (values) => {
    state.start = values[0].toDate();
    state.end = values[1].toDate();
    updateHistory();
  };

  return <div ref={divRef} className="xm-alert-history">
    <div className="xm-alert-history-range">
      Show alerts within time range: <XmRangeDatePicker
        presets={alertHistoryRanges}
        allowClear={false}
        defaultValue={[dayjs(state.start), dayjs(state.end)]}
        onChange={handleRangeChange}
      />
    </div>
    <gs.Grid
      dataSource={history}
      rowKey={(r) => r.alerting_history_id}
      columns={cols}
      loading={loading}
      sticky
      scroll={divRef.current ? { y: divRef.current.closest('.ant-tabs-content').clientHeight - 65 } : null}
    ></gs.Grid>
  </div>;
};

const alertHistoryRanges: RangePickerProps['presets'] = RANGE_DATE_PICKER_DEFAULT_PRESETS;

function ItemsTagRender(items: OverlimitItem[], useOverlimit = false, metric?: Metric) {
  return (props: Readonly<CustomTagProps>) => SelectItemTagRender(props, items, useOverlimit, metric);
}

export function SelectItemTagRender(props: Readonly<CustomTagProps>, items?: OverlimitItem[], useOverlimit = false, metric?: Metric) {
  const item = items?.find(i => i.item_id === props.value);
  const isANumber = typeof item?.overlimit === 'number' || typeof item?.overlimit === 'string' && !isNaN(Number(item.overlimit));
  const fallback = props.label ?? props.value;
  return <span className="ant-select-selection-item">{useOverlimit && item?.overlimit ? item?.label + ` [${isANumber && metric?.unit !== "count" ? UnitUtil.formatSI(Number(item?.overlimit), 2, false, metric) : item.overlimit}]` : item?.label || fallback}</span>;
}

function triggeredSorter(a: AlertHistory, b: AlertHistory) {
  return a.triggered.localeCompare(b.triggered);
}

function alertGroupSorter(a: AlertHistory, b: AlertHistory) {
  return a.alerting_group_label.localeCompare(b.alerting_group_label);
}

function alertGroupTypeSorter(a: AlertHistory, b: AlertHistory) {
  return a.alerting_group_alert_type.localeCompare(b.alerting_group_alert_type);
}

function metricSorter(a: AlertHistory, b: AlertHistory) {
  return a.metric.label.localeCompare(b.metric.label);
}

function userGroupSorter(a: AlertHistory, b: AlertHistory) {
  return a.group_label?.localeCompare(b.group_label);
}

function path(selector: (props: AlertHistory) => unknown) {
  return nameof<AlertHistory>(selector).split('.');
}